import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { environment } from '../../../environments/environment';
import AddressPickerWithGoogle from '../../components/Address/GoogleAutoComplete';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneInternational from '../../components/PhoneNumber/phoneinternational';
const CateringForm = () => {
  const [googleAddress, setGoogleAddress] = useState('');
  const [formikRef, setFormikRef] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');

  const getGoogleAddress = ({
    country,
    state,
    city,
    zipCode,
    placeId,
    displayName,
    lat,
    lng,
    streetNumber,
    route,
  }) => {
    const addressData = {
      country,
      state,
      city,
      zipCode,
      placeId,
      displayName,
      lat,
      lng,
      streetNumber,
      route,
    };
    setGoogleAddress(addressData);
  };

  // Update form fields when Google address changes
  useEffect(() => {
    if (googleAddress && Object.keys(googleAddress).length > 0 && formikRef) {
      // Populate street address
      const streetAddress =
        googleAddress.streetNumber && googleAddress.route
          ? `${googleAddress.streetNumber} ${googleAddress.route}`
          : googleAddress.route || '';
      if (streetAddress) {
        formikRef.setFieldValue('streetAddress', streetAddress);
        formikRef.setFieldTouched('streetAddress', false);
      }

      // Populate city
      if (googleAddress.city) {
        formikRef.setFieldValue('city', googleAddress.city);
        formikRef.setFieldTouched('city', false);
      }

      // Populate state
      if (googleAddress.state) {
        formikRef.setFieldValue('state', googleAddress.state);
        formikRef.setFieldTouched('state', false);
      }

      // Populate zip code
      if (googleAddress.zipCode) {
        formikRef.setFieldValue('zipCode', googleAddress.zipCode);
        formikRef.setFieldTouched('zipCode', false);
      }
    }
  }, [googleAddress, formikRef]);

  // Update phone validation when phone number changes
  useEffect(() => {
    if (formikRef && phoneNumber !== undefined) {
      formikRef.setFieldValue('phone', phoneNumber);
      if (phoneNumber) {
        formikRef.setFieldTouched('phone', true);
      }
    }
  }, [phoneNumber, formikRef]);

  return (
    <Grid
      item
      xs={12}
      sx={{
        position: 'relative',
        width: '85%',
        mx: 'auto',
        mt: { xs: '-100px', sm: '-150px', md: '-250px' },
        backgroundColor: 'white',

        borderColor: 'divider',
        borderRadius: '16px',
        overflow: 'hidden',
        py: 7,
        px: { xs: 3, sm: 5, md: 7 },
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        fontFamily: '"Jost", sans-serif',
        boxShadow: '0px 4px 100px 0px #00000014',
        zIndex: 2,
      }}
    >
      <Box>
        <Typography
          variant="h2"
          align="center"
          fontWeight={800}
          sx={{
            fontFamily: '"Jost", sans-serif',
            color: '#172B4D',
            fontSize: '32px',
          }}
        >
          Request Catering
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: '#606F8A',
            mt: 1,
            fontFamily: '"Jost", sans-serif',
            textTransform: 'capatalize',
            fontSize: { xs: '12px', md: '14px' },
          }}
        >
          Please Provide The Following Information And We'll Get Back To You As
          Soon As Possible.
        </Typography>
      </Box>

      <Formik
        initialValues={{
          name: '',
          email: '',
          phone: '',
          streetAddress: '',
          city: '',
          state: '',
          zipCode: '',
          date: null,
          time: null,
          guests: '',
          message: '',
        }}
        validationSchema={Yup.object({
          name: Yup.string()
            .trim()
            .required('Name is required')
            .min(1, 'Name cannot be empty'),
          email: Yup.string()
            .trim()
            .email('Invalid email format')
            .required('Email is required'),
          phone: Yup.string()
            .trim()
            .required('Phone number is required')
            .test(
              'valid-phone',
              'Phone number must contain 3-11 digits',
              function (value) {
                if (!value) return false;
                const digitsOnly = value.replace(/[^\d]/g, '');
                return digitsOnly.length >= 3 && digitsOnly.length <= 14;
              }
            ),
          streetAddress: Yup.string()
            .trim()
            .required('Street address is required')
            .min(1, 'Street address cannot be empty'),
          city: Yup.string()
            .trim()
            .required('City is required')
            .min(1, 'City cannot be empty'),
          state: Yup.string()
            .trim()
            .required('State is required')
            .min(1, 'State cannot be empty'),
          zipCode: Yup.string()
            .trim()
            .required('Zip code is required')
            .matches(/^\d{5}(-\d{4})?$/, 'Invalid zip code format'),
          date: Yup.mixed().required('Event date is required'),
          time: Yup.mixed().required('Event time is required'),
          guests: Yup.number()
            .typeError('Must be a number')
            .positive('Number of guests must be positive')
            .integer('Number of guests must be a whole number')
            .required('Number of guests is required'),
          message: Yup.string().trim(),
        })}
        enableReinitialize={false}
        onSubmit={async (
          values,
          { setSubmitting, resetForm, setFieldError }
        ) => {
          try {
            setSubmitting(true);

            // Format address in detailed US format from individual fields
            const addressParts = [];
            if (values.streetAddress.trim())
              addressParts.push(values.streetAddress.trim());
            if (values.city.trim()) addressParts.push(values.city.trim());
            if (values.state.trim()) addressParts.push(values.state.trim());
            if (values.zipCode.trim()) addressParts.push(values.zipCode.trim());
            const detailedAddress = addressParts.join(', ');

            // Format the data according to the API requirements
            const formattedData = {
              name: values.name.trim(),
              email: values.email.trim(),
              phoneNumber: phoneNumber || values.phone.trim(),
              eventLocation: detailedAddress,
              eventDate: values.date
                ? dayjs(values.date).format('YYYY-MM-DD')
                : '',
              eventTime: values.time
                ? dayjs(values.time).format('hh:mm A')
                : '',
              numberOfGuests: parseInt(values.guests, 10),
              message: values.message ? values.message.trim() : '',
            };

            // Make API call with restaurantId as query param
            const response = await fetch(
              `${environment.apiUrl}/cateringRequest?restaurantId=${environment.restaurantId}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(formattedData),
              }
            );

            const result = await response.json();

            if (response.ok) {
              toast.success(
                'Catering request submitted successfully! We will get back to you soon.'
              );
              resetForm();
              setGoogleAddress('');
              setPhoneNumber('');
            } else {
              // Handle API errors
              if (result.errors && Array.isArray(result.errors)) {
                // Handle field-specific errors
                result.errors.forEach((error) => {
                  if (error.field) {
                    setFieldError(error.field, error.message);
                  } else {
                    toast.error(
                      error.message ||
                        'An error occurred while submitting your request.'
                    );
                  }
                });
              } else {
                toast.error(
                  result.message ||
                    'Failed to submit catering request. Please try again.'
                );
              }
            }
          } catch (error) {
            console.error('Catering request submission error:', error);
            toast.error(
              'Network error. Please check your connection and try again.'
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {(formikProps) => {
          const {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          } = formikProps;

          // Set formik ref for external access
          if (!formikRef) {
            setFormikRef(formikProps);
          }

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Your Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <PersonOutlineIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <PhoneInternational
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        backgroundColor="white"
                      />
                      {touched.phone && errors.phone && (
                        <Typography
                          variant="caption"
                          sx={{
                            marginTop: '8px',
                            color: '#f44336',
                            fontSize: '12px',
                            fontFamily: 'Jost, sans-serif',
                            display: 'block',
                            marginLeft: '4px',
                          }}
                        >
                          {errors.phone}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="guests"
                      label="Number of guests"
                      value={values.guests}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.guests && Boolean(errors.guests)}
                      helperText={touched.guests && errors.guests}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <GroupOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <AddressPickerWithGoogle
                        getValues={getGoogleAddress}
                        initialValue={''}
                        placeholder="Search event location (US addresses)"
                        backgroundColor="white"
                        borderColor={'#EAEAEA'}
                        borderRadius="5px"
                        borderWidth={1}
                        hoverBorderColor={'black'}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="streetAddress"
                      label="Street Address"
                      placeholder="e.g., 123 Main Street"
                      value={values.streetAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.streetAddress && Boolean(errors.streetAddress)
                      }
                      helperText={touched.streetAddress && errors.streetAddress}
                      InputProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="city"
                      label="City"
                      placeholder="e.g., New York"
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                      InputProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      name="state"
                      label="State"
                      placeholder="e.g., NY"
                      value={values.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                      InputProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      name="zipCode"
                      label="Zip Code"
                      placeholder="e.g., 10001"
                      value={values.zipCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.zipCode && Boolean(errors.zipCode)}
                      helperText={touched.zipCode && errors.zipCode}
                      InputProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <DatePicker
                        value={values.date}
                        onChange={(newValue) => setFieldValue('date', newValue)}
                        minDate={dayjs()}
                        slots={{
                          textField: TextField,
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: touched.date && Boolean(errors.date),
                            helperText: touched.date && errors.date,
                            placeholder: 'Select your event date',
                            InputLabelProps: {
                              shrink: true,
                              style: { display: 'none' },
                            },
                            InputProps: {
                              sx: {
                                height: '64px',

                                border: `1px solid ${
                                  touched.date && errors.date
                                    ? 'black'
                                    : '#EAEAEA'
                                }`,
                                transition:
                                  'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                                '&.Mui-focused': {
                                  borderColor:
                                    environment.mainColor || '#1976d2',
                                  backgroundColor: '#FFFFFF',
                                  boxShadow: `0 0 0 4px ${
                                    environment.mainColor || '#1976d2'
                                  }15, 0 8px 25px rgba(0,0,0,0.12)`,
                                  transform: 'translateY(-2px)',
                                },
                                '& fieldset': {
                                  border: 'none',
                                },
                                '& .MuiInputBase-input': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontSize: '16px',
                                  fontWeight: 500,
                                  color: '#172B4D',
                                  padding: '20px 16px',
                                  cursor: 'pointer',
                                  width: '100%',
                                  '&::placeholder': {
                                    color: '#9E9E9E',
                                    opacity: 1,
                                  },
                                },
                                '& .MuiInputAdornment-root': {
                                  marginRight: '12px',
                                  '& .MuiButtonBase-root': {
                                    color: environment.mainColor || '#1976d2',
                                    fontSize: '26px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      color: '#1565c0',
                                      transform: 'scale(1.15)',
                                      backgroundColor: 'transparent',
                                    },
                                  },
                                },
                              },
                            },
                            FormHelperTextProps: {
                              style: {
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '12px',
                                marginTop: '8px',
                                marginLeft: '4px',
                              },
                            },
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                borderRadius: '20px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                border: `3px solid ${
                                  environment.mainColor || '#1976d2'
                                }20`,
                                overflow: 'hidden',
                                marginTop: '8px',
                              },
                              '& .MuiPickersCalendarHeader-root': {
                                backgroundColor:
                                  environment.mainColor || '#1976d2',
                                color: 'white',
                                padding: '20px',
                                '& .MuiPickersArrowSwitcher-button': {
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                  },
                                },
                                '& .MuiPickersCalendarHeader-label': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 700,
                                  fontSize: '18px',
                                },
                              },
                              '& .MuiDayCalendar-header': {
                                padding: '16px 0',
                                '& .MuiTypography-root': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 700,
                                  color: environment.mainColor || '#1976d2',
                                  fontSize: '14px',
                                },
                              },
                              '& .MuiPickersDay-root': {
                                fontFamily: 'Jost, sans-serif',
                                fontWeight: 500,
                                fontSize: '15px',
                                width: '40px',
                                height: '40px',
                                margin: '2px',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease-in-out',
                                '&.Mui-selected': {
                                  backgroundColor:
                                    environment.mainColor || '#1976d2',
                                  boxShadow: `0 4px 12px ${
                                    environment.mainColor || '#1976d2'
                                  }40`,
                                  '&:hover': {
                                    backgroundColor:
                                      environment.mainColor || '#1976d2',
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: `${
                                    environment.mainColor || '#1976d2'
                                  }15`,
                                  transform: 'scale(1.1)',
                                },
                                '&.MuiPickersDay-today': {
                                  border: `2px solid ${
                                    environment.mainColor || '#1976d2'
                                  }`,
                                  fontWeight: 700,
                                },
                              },
                              '& .MuiPickersCalendarHeader-switchViewButton': {
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                },
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <TimePicker
                        value={values.time}
                        onChange={(newValue) => setFieldValue('time', newValue)}
                        slots={{
                          textField: TextField,
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: touched.time && Boolean(errors.time),
                            helperText: touched.time && errors.time,
                            placeholder: 'Select your event time',
                            InputLabelProps: {
                              shrink: true,
                              style: { display: 'none' },
                            },
                            InputProps: {
                              sx: {
                                height: '64px',

                                border: `1px solid ${
                                  touched.date && errors.date
                                    ? 'black'
                                    : '#EAEAEA'
                                }`,

                                '&.Mui-focused': {
                                  borderColor:
                                    environment.mainColor || '#1976d2',
                                  backgroundColor: '#FFFFFF',
                                  boxShadow: `0 0 0 4px ${
                                    environment.mainColor || '#1976d2'
                                  }15, 0 8px 25px rgba(0,0,0,0.12)`,
                                },
                                '& fieldset': {
                                  border: 'none',
                                },
                                '& .MuiInputBase-input': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontSize: '16px',
                                  fontWeight: 500,
                                  color: '#172B4D',
                                  padding: '20px 16px',
                                  cursor: 'pointer',
                                  width: '100%',
                                  '&::placeholder': {
                                    color: '#9E9E9E',
                                    opacity: 1,
                                  },
                                },
                                '& .MuiInputAdornment-root': {
                                  marginRight: '12px',
                                  '& .MuiButtonBase-root': {
                                    color: environment.mainColor || '#1976d2',
                                    fontSize: '26px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      color: '#1565c0',
                                      transform: 'scale(1.15)',
                                      backgroundColor: 'transparent',
                                    },
                                  },
                                },
                              },
                            },
                            FormHelperTextProps: {
                              style: {
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '12px',
                                marginTop: '8px',
                                marginLeft: '4px',
                              },
                            },
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                borderRadius: '20px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                border: `3px solid ${
                                  environment.mainColor || '#1976d2'
                                }20`,
                                overflow: 'hidden',
                                marginTop: '8px',
                              },
                              '& .MuiTimeClock-root': {
                                padding: '20px',
                                '& .MuiClock-pin': {
                                  backgroundColor:
                                    environment.mainColor || '#1976d2',
                                },
                                '& .MuiClockPointer-root': {
                                  backgroundColor:
                                    environment.mainColor || '#1976d2',
                                },
                                '& .MuiClockPointer-thumb': {
                                  backgroundColor:
                                    environment.mainColor || '#1976d2',
                                  borderColor:
                                    environment.mainColor || '#1976d2',
                                  width: '24px',
                                  height: '24px',
                                  border: '4px solid white',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                },
                                '& .MuiClock-clock': {
                                  backgroundColor: '#FAFBFC',
                                  border: `3px solid ${
                                    environment.mainColor || '#1976d2'
                                  }10`,
                                },
                              },
                              '& .MuiPickersTimeClock-root': {
                                '& .MuiClockNumber-root': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 600,
                                  fontSize: '16px',
                                  '&.Mui-selected': {
                                    backgroundColor:
                                      environment.mainColor || '#1976d2',
                                    color: 'white',
                                    boxShadow: `0 4px 12px ${
                                      environment.mainColor || '#1976d2'
                                    }40`,
                                  },
                                  '&:hover': {
                                    backgroundColor: `${
                                      environment.mainColor || '#1976d2'
                                    }20`,
                                  },
                                },
                              },
                              '& .MuiDialogActions-root': {
                                padding: '20px 24px',
                                backgroundColor: '#F8F9FA',
                                '& .MuiButton-root': {
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 600,
                                  color: environment.mainColor || '#1976d2',
                                  borderRadius: '12px',
                                  padding: '8px 20px',
                                  '&:hover': {
                                    backgroundColor: `${
                                      environment.mainColor || '#1976d2'
                                    }15`,
                                  },
                                },
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="message"
                      label="Type Your Message"
                      value={values.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      minRows={4}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      InputProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EAEAEA',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { fontFamily: 'Jost, sans-serif' },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Box mt={2} display="flex" justifyContent="flex-start">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 8,
                      px: 5,
                      background: environment.mainColor,
                      padding: '9px 16px',
                      fontFamily: '"Jost", sans-serif',
                      fontWeight: 700,
                      width: '126px',
                      boxShadow: 'none',
                      textTransform: 'capitalize',
                      '&:hover': {
                        background: environment.mainColor,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </Box>
              </form>
            </LocalizationProvider>
          );
        }}
      </Formik>

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ fontFamily: 'Jost, sans-serif' }}
      />
    </Grid>
  );
};

export default CateringForm;
